import { useState, useEffect, useCallback } from "react";
import {
  Plus, Trash2, Zap, CheckCircle2, XCircle, RefreshCw,
  ChevronDown, ChevronRight, Terminal, Globe, Cpu,
  Play, Loader2, X, AlertCircle, Server, Key, Copy, Check as CheckIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import { mcpAPI, personalMcpAPI } from "../services/api";

// ── Figma logo (SVG) ──────────────────────────────────────────────────────────
function FigmaLogo({ size = 20 }) {
  const s = size;
  return (
    <svg width={s} height={s} viewBox="0 0 38 57" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 28.5a9.5 9.5 0 1 1 19 0 9.5 9.5 0 0 1-19 0z" fill="#1ABCFE" />
      <path d="M0 47.5A9.5 9.5 0 0 1 9.5 38H19v9.5a9.5 9.5 0 1 1-19 0z" fill="#0ACF83" />
      <path d="M19 0h9.5a9.5 9.5 0 1 1 0 19H19V0z" fill="#FF7262" />
      <path d="M0 9.5A9.5 9.5 0 0 1 9.5 0H19v19H9.5A9.5 9.5 0 0 1 0 9.5z" fill="#F24E1E" />
      <path d="M0 28.5A9.5 9.5 0 0 1 9.5 19H19v19H9.5A9.5 9.5 0 0 1 0 28.5z" fill="#A259FF" />
    </svg>
  );
}

// ── Theme ─────────────────────────────────────────────────────────────────────
const C = {
  blue:     "var(--lyra-color-primary)",
  blueBg:   "var(--app-primary-soft)",
  blueBord: "var(--app-primary-soft-2)",
  green:    "var(--lyra-color-success)",
  greenBg:  "var(--lyra-color-success-bg)",
  red:      "var(--lyra-color-danger)",
  redBg:    "var(--lyra-color-danger-bg)",
  tx:       "var(--lyra-color-text)",
  mu:       "var(--app-text-3)",
  su:       "var(--lyra-color-text-muted)",
  bord:     "var(--lyra-color-border)",
  bg:       "var(--lyra-color-surface-2)",
  card:     "var(--lyra-color-surface)",
};

// ── Transport meta ─────────────────────────────────────────────────────────────
const TRANSPORTS = {
  sse:   { label: "SSE",          icon: Globe,    desc: "HTTP + Server-Sent Events — for remote MCP servers" },
  http:  { label: "Streamable HTTP", icon: Globe, desc: "Streamable HTTP transport — for newer MCP servers" },
  stdio: { label: "stdio",        icon: Terminal, desc: "Child process — for local MCP servers on the same machine" },
};

// ── Small helpers ──────────────────────────────────────────────────────────────
function Badge({ label, bg, color }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: bg, color }}>
      {label}
    </span>
  );
}

function Btn({ onClick, disabled, loading, icon: Icon, label, variant = "primary", small }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    border: "none", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 600, transition: "opacity 0.15s", fontFamily: "inherit",
    opacity: disabled || loading ? 0.6 : 1,
    fontSize: small ? 12 : 13,
    padding: small ? "5px 10px" : "8px 14px",
  };
  const styles = {
    primary: { ...base, background: C.blue,    color: "var(--app-on-accent)" },
    danger:  { ...base, background: C.redBg,   color: C.red, border: `1px solid var(--app-danger-border)` },
    ghost:   { ...base, background: "transparent", color: C.su, border: `1px solid ${C.bord}` },
  };
  return (
    <button style={styles[variant]} onClick={onClick} disabled={disabled || loading}>
      {loading ? <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> : Icon && <Icon size={13} />}
      {label}
    </button>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", monospace }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: C.su }}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: "7px 10px", border: `1px solid ${C.bord}`, borderRadius: 8,
          fontSize: 13, color: C.tx, outline: "none", background: "var(--lyra-color-surface)",
          fontFamily: monospace ? "monospace" : "inherit",
        }}
      />
    </label>
  );
}

// ── Figma Quick-Add Preset (PAT flow) ────────────────────────────────────────
function FigmaPreset({ servers, onAdded, onRemoved, isMobile = false }) {
  const existing    = servers.find(s => s.name === "figma");
  const isConnected = !!existing && !existing.lastError;
  const [pat,      setPat]      = useState("");
  const [show,     setShow]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [username, setUsername] = useState(existing ? null : null);

  // load saved username on mount
  useEffect(() => {
    mcpAPI.figmaPatStatus().then(d => { if (d.username) setUsername(d.username); }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!pat.trim()) return;
    setLoading(true);
    try {
      const data = await mcpAPI.figmaPatSave(pat.trim());
      setUsername(data.username);
      setPat("");
      setShow(false);
      toast.success(`Figma connected as @${data.username} — ${data.toolCount} tools ready`);
      onAdded(data.server);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setLoading(true);
    try {
      await mcpAPI.figmaPatDelete();
      setUsername(null);
      setPat("");
      setShow(false);
      toast.success("Figma disconnected");
      onRemoved();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: "var(--lyra-color-surface)",
      border: `1.5px solid ${isConnected ? C.blueBord : C.bord}`,
      borderRadius: 14,
      padding: isMobile ? "12px 12px" : "16px 18px",
      boxShadow: isConnected ? "0 0 0 3px var(--app-primary-soft)" : "0 1px 4px rgba(0,0,0,0.05)",
      display: "flex",
      gap: isMobile ? 10 : 14,
      alignItems: "flex-start",
      flexDirection: isMobile ? "column" : "row",
    }}>
      {/* Icon */}
      <div style={{
        width: 42, height: 42, borderRadius: 11, flexShrink: 0,
        background: isConnected ? "var(--app-primary-soft)" : "var(--app-primary-soft)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <FigmaLogo size={22} />
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.tx }}>Figma</span>
          <Badge label="PAT" bg="var(--app-primary-soft)" color="var(--lyra-color-primary)" />
          {isConnected && <Badge label="CONNECTED" bg={C.greenBg} color={C.green} />}
        </div>

        <div style={{ fontSize: 12, color: C.mu, lineHeight: 1.5, marginBottom: 10 }}>
          Read files, frames, components, styles &amp; variables from your Figma workspace
          using a <strong style={{ color: C.tx }}>Personal Access Token</strong>.{" "}
          <a href="https://www.figma.com/settings" target="_blank" rel="noreferrer"
             style={{ color: "var(--lyra-color-primary)", textDecoration: "none" }}>
            Generate one in Figma Settings → Account → Personal access tokens
          </a>
        </div>

        {isConnected && username && (
          <div style={{ fontSize: 12, color: C.mu, marginBottom: 10 }}>
            Connected as <strong style={{ color: C.tx }}>@{username}</strong>
            {" · "}{existing?.tools?.length || 0} tools available
          </div>
        )}

        {/* PAT input (shown when not connected, or when updating) */}
        {(!isConnected || show) && (
          <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexDirection: isMobile ? "column" : "row" }}>
            <input
              type="password"
              placeholder="figd_xxxxxxxxxxxx"
              value={pat}
              onChange={e => setPat(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSave()}
              style={{
                flex: 1, height: 34, borderRadius: 8, border: `1px solid ${C.bord}`,
                padding: "0 10px", fontSize: 12.5, outline: "none",
                fontFamily: "monospace", color: C.tx, background: "var(--lyra-color-surface)",
                width: "100%",
              }}
            />
            <Btn
              label={loading ? "Saving…" : "Save"}
              loading={loading}
              onClick={handleSave}
              variant="primary"
              small={isMobile}
            />
            {show && (
              <button onClick={() => { setShow(false); setPat(""); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: C.mu, padding: 4 }}>
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {isConnected && !show && (
          <div style={{ display: "flex", gap: 8, flexDirection: isMobile ? "column" : "row" }}>
            <Btn label="Update token" onClick={() => setShow(true)} variant="ghost" small={isMobile} />
            <Btn label="Disconnect" onClick={handleRemove} loading={loading} variant="danger" small={isMobile} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add Server Modal ──────────────────────────────────────────────────────────
function AddServerModal({ onClose, onAdded, isMobile = false }) {
  const [form, setForm] = useState({
    name: "", displayName: "", description: "",
    transport: "sse", url: "", command: "npx", args: "", headers: "", env: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name || !form.displayName) return toast.error("Name and Display Name are required");
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        displayName: form.displayName,
        description: form.description,
        transport: form.transport,
        url: form.url,
        command: form.command,
        args: form.args ? form.args.split(/\s+/).filter(Boolean) : [],
        headers: form.headers ? JSON.parse(form.headers) : {},
        env: form.env ? JSON.parse(form.env) : {},
      };
      const data = await mcpAPI.addServer(payload);
      toast.success(`"${data.server.displayName}" registered`);
      onAdded(data.server);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isStdio = form.transport === "stdio";
  const isHttp  = form.transport === "sse" || form.transport === "http";

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "center", zIndex: 1000,
      padding: isMobile ? "10px" : 0,
    }}>
      <div style={{
        background: "var(--lyra-color-surface)", borderRadius: 16, padding: isMobile ? 14 : 28, width: isMobile ? "100%" : 520,
        maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.tx }}>Register MCP Server</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.mu }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <Input label="Slug name *" value={form.name} onChange={set("name")} placeholder="filesystem" monospace />
            <Input label="Display name *" value={form.displayName} onChange={set("displayName")} placeholder="Filesystem Tools" />
          </div>
          <Input label="Description" value={form.description} onChange={set("description")} placeholder="Optional description" />

          {/* Transport selector */}
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.su }}>Transport *</span>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0,1fr))", gap: 8 }}>
              {Object.entries(TRANSPORTS).map(([key, meta]) => {
                const Icon = meta.icon;
                const active = form.transport === key;
                return (
                  <button
                    key={key}
                    onClick={() => setForm(f => ({ ...f, transport: key }))}
                    style={{
                      flex: 1, padding: "8px 6px", borderRadius: 8, cursor: "pointer",
                      border: `1.5px solid ${active ? C.blue : C.bord}`,
                      background: active ? C.blueBg : "var(--lyra-color-surface)",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                    }}
                  >
                    <Icon size={14} color={active ? C.blue : C.su} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: active ? C.blue : C.su }}>{meta.label}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: C.mu }}>{TRANSPORTS[form.transport].desc}</div>
          </div>

          {isHttp && (
            <>
              <Input label="URL *" value={form.url} onChange={set("url")} placeholder="http://localhost:3010/sse" monospace />
              <Input label="Headers (JSON)" value={form.headers} onChange={set("headers")} placeholder='{"Authorization": "Bearer token"}' monospace />
            </>
          )}

          {isStdio && (
            <>
              <Input label="Command *" value={form.command} onChange={set("command")} placeholder="npx" monospace />
              <Input label="Args (space-separated)" value={form.args} onChange={set("args")} placeholder="-y @modelcontextprotocol/server-filesystem /tmp" monospace />
              <Input label="Env vars (JSON)" value={form.env} onChange={set("env")} placeholder='{"HOME": "/tmp"}' monospace />
            </>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          <Btn variant="ghost" label="Cancel" onClick={onClose} small={isMobile} />
          <Btn label="Register" loading={loading} onClick={handleSubmit} small={isMobile} />
        </div>
      </div>
    </div>
  );
}

// ── Personal MCP Panel ────────────────────────────────────────────────────────
const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button onClick={copy} title="Copy" style={{
      background: "none", border: "none", cursor: "pointer", color: copied ? C.green : C.mu,
      padding: "2px 5px", display: "flex", alignItems: "center",
    }}>
      {copied ? <CheckIcon size={13} /> : <Copy size={13} />}
    </button>
  );
}

export function PersonalMcpPanel({ isMobile = false, embedded = false }) {
  const [keys,        setKeys]       = useState([]);
  const [tools,       setTools]      = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [generating,  setGenerating] = useState(false);
  const [newRawKey,   setNewRawKey]  = useState(null); // shown ONCE after generation
  const [open,        setOpen]       = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kd, td] = await Promise.all([personalMcpAPI.listKeys(), personalMcpAPI.listTools()]);
      setKeys(kd.keys || []);
      setTools(td.tools || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateKey = async () => {
    setGenerating(true);
    try {
      const data = await personalMcpAPI.createKey();
      setNewRawKey(data.key);
      setKeys(prev => [{ _id: data.id, createdAt: data.createdAt, scopes: data.scopes }, ...prev]);
      toast.success('API key generated — copy it now, it won\'t be shown again');
    } catch { toast.error('Failed to generate key'); }
    finally { setGenerating(false); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this key? Any clients using it will lose access.')) return;
    try {
      await personalMcpAPI.deleteKey(id);
      setKeys(prev => prev.filter(k => k._id !== id));
      if (newRawKey) setNewRawKey(null);
      toast.success('Key revoked');
    } catch { toast.error('Failed to revoke key'); }
  };

  const sseUrl = keys[0] ? `${BASE_URL}/mcp/${keys[0]._id ? '' : ''}` : null;
  // Use newRawKey if available (just generated), else we can't reconstruct the raw key
  const displayKey = newRawKey || (keys[0] ? '(key hidden — generate a new one to copy)' : null);

  const content = (
        <div style={{ padding: embedded ? 0 : (isMobile ? "12px" : "18px 20px"), display: "flex", flexDirection: "column", gap: 18 }}>

          {/* API Keys section */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.su, textTransform: "uppercase", letterSpacing: "0.06em" }}>API Keys</span>
              <Btn icon={Plus} label={generating ? "Generating…" : "Generate key"} loading={generating} onClick={generateKey} small />
            </div>

            {/* Newly generated key — shown once */}
            {newRawKey && (
              <div style={{ background: "var(--lyra-color-warning-bg)", border: "1px solid var(--app-warning-border)", borderRadius: 10, padding: "10px 14px", marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--lyra-color-warning)", marginBottom: 5 }}>
                  ⚠ Copy this key now — it won't be shown again
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexDirection: isMobile ? "column" : "row" }}>
                  <code style={{ flex: 1, fontSize: 12, fontFamily: "monospace", color: "var(--lyra-color-text)", wordBreak: "break-all" }}>{newRawKey}</code>
                  <CopyBtn text={newRawKey} />
                </div>
              </div>
            )}

            {loading ? (
              <div style={{ color: C.mu, fontSize: 12 }}>Loading…</div>
            ) : keys.length === 0 ? (
              <div style={{ fontSize: 12, color: C.mu, padding: "10px 0" }}>No keys yet. Generate one to start connecting.</div>
            ) : (
              <div style={{ border: `1px solid ${C.bord}`, borderRadius: 10, overflow: "hidden" }}>
                {keys.map((k, i) => {
                  const sseForKey = newRawKey && i === 0 ? `${BASE_URL}/mcp/${newRawKey}/sse` : null;
                  return (
                    <div key={k._id} style={{
                      display: "flex", alignItems: isMobile ? "flex-start" : "center", gap: 10,
                      flexDirection: isMobile ? "column" : "row",
                      padding: "9px 12px",
                      borderBottom: i < keys.length - 1 ? `1px solid ${C.bord}` : "none",
                      background: i % 2 === 0 ? "var(--lyra-color-surface)" : "var(--lyra-color-surface)",
                    }}>
                      <Key size={12} color={C.mu} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {sseForKey ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <code style={{ fontSize: 11, fontFamily: "monospace", color: C.tx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {sseForKey}
                            </code>
                            <CopyBtn text={sseForKey} />
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: C.mu, fontFamily: "monospace" }}>
                            ···· (created {new Date(k.createdAt).toLocaleDateString()})
                          </span>
                        )}
                        {k.lastUsed && (
                          <div style={{ fontSize: 10, color: C.mu }}>Last used: {new Date(k.lastUsed).toLocaleString()}</div>
                        )}
                      </div>
                      <button onClick={() => revokeKey(k._id)} title="Revoke"
                        style={{ background: "none", border: "none", cursor: "pointer", color: C.mu, padding: 4, flexShrink: 0 }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Exposed agents / tools */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.su, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
              Exposed Agents ({tools.length})
            </div>
            {tools.length === 0 ? (
              <div style={{
                padding: "14px 16px", background: C.bg, borderRadius: 10, border: `1px dashed ${C.bord}`,
                fontSize: 12, color: C.mu, textAlign: "center",
              }}>
                No agents exposed yet.{" "}
                <a href="/agents" style={{ color: C.blue, textDecoration: "none", fontWeight: 600 }}>
                  Open Agent Builder
                </a>
                {" "}→ Runtime Config → MCP Exposure → ON → Save.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {tools.map(tool => (
                  <div key={tool.name} style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "10px 14px", background: C.bg, borderRadius: 10, border: `1px solid ${C.bord}`,
                  }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: C.blueBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Zap size={13} color={C.blue} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                        <code style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 700, color: C.tx }}>{tool.name}</code>
                        <Badge label="MCP tool" bg={C.blueBg} color={C.blue} />
                      </div>
                      {tool.description && <div style={{ fontSize: 12, color: C.su }}>{tool.description}</div>}
                      <div style={{ fontSize: 11, color: C.mu, marginTop: 3 }}>
                        Input: {Object.keys(tool.inputSchema?.properties || {}).join(', ') || 'input (string)'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Claude Desktop config snippet */}
          {newRawKey && (
            <div style={{ background: "var(--lyra-color-surface)", border: `1px solid ${C.bord}`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.su, marginBottom: 6 }}>Claude Desktop config</div>
              <div style={{ position: "relative" }}>
                <pre style={{ margin: 0, fontSize: 11, fontFamily: "monospace", color: C.tx, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{
`{
  "mcpServers": {
    "lyra-agents": {
      "url": "${BASE_URL}/mcp/${newRawKey}/sse"
    }
  }
}`
                }</pre>
                <CopyBtn text={`{\n  "mcpServers": {\n    "lyra-agents": {\n      "url": "${BASE_URL}/mcp/${newRawKey}/sse"\n    }\n  }\n}`} />
              </div>
            </div>
          )}
        </div>
  );

  if (embedded) return content;

  return (
    <div style={{
      background: "var(--lyra-color-surface)", border: `1.5px solid var(--app-primary-soft-2)`,
      borderRadius: 14, marginBottom: 24, overflow: "hidden",
      boxShadow: "0 0 0 3px var(--app-primary-soft)",
    }}>
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 12,
          padding: isMobile ? "12px 12px" : "16px 20px", background: "none", border: "none", cursor: "pointer",
          borderBottom: open ? `1px solid ${C.bord}` : "none",
        }}
      >
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--app-primary-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Key size={18} color={C.blue} />
        </div>
        <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.tx }}>Your Personal MCP Server</span>
            <Badge label="SSE" bg={C.blueBg} color={C.blue} />
            {tools.length > 0 && <Badge label={`${tools.length} agent${tools.length !== 1 ? 's' : ''} exposed`} bg={C.greenBg} color={C.green} />}
          </div>
          <div style={{ fontSize: 12, color: C.mu, marginTop: 2 }}>
            Connect Claude Desktop or any MCP client to run your agents as tools
          </div>
        </div>
        {open ? <ChevronDown size={16} color={C.mu} /> : <ChevronRight size={16} color={C.mu} />}
      </button>

      {open && content}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function ToolTester({ serverId, serverName, isMobile = false }) {
  const [tools, setTools]       = useState([]);
  const [selected, setSelected] = useState("");
  const [args, setArgs]         = useState("{}");
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);

  useEffect(() => {
    if (!open) return;
    mcpAPI.getTools(serverId).then(d => {
      const list = d.tools || [];
      setTools(list);
      if (list[0]) setSelected(list[0].name);
    }).catch(() => {});
  }, [open, serverId]);

  // Re-build default args whenever the selected tool changes
  useEffect(() => {
    const tool = tools.find(t => t.name === selected);
    if (!tool) return;
    const props = tool.inputSchema?.properties || {};
    const defaults = {};
    for (const [key, schema] of Object.entries(props)) {
      if (schema.default !== undefined) defaults[key] = schema.default;
    }
    // Also pre-fill required fields that have no default with a type hint
    const required = tool.inputSchema?.required || [];
    for (const key of required) {
      if (defaults[key] === undefined) {
        const type = props[key]?.type;
        defaults[key] = type === "number" ? 0 : type === "boolean" ? false : type === "array" ? [] : type === "object" ? {} : "";
      }
    }
    setArgs(JSON.stringify(Object.keys(defaults).length > 0 ? defaults : {}, null, 2));
    setResult(null);
  }, [selected, tools]);

  const selectedTool = tools.find(t => t.name === selected);
  const schemaProps  = selectedTool?.inputSchema?.properties || {};
  const required     = new Set(selectedTool?.inputSchema?.required || []);

  const handleCall = async () => {
    if (!selected) return;
    setLoading(true);
    setResult(null);
    try {
      let parsed;
      try { parsed = JSON.parse(args); } catch { return toast.error("Args must be valid JSON"); }
      const data = await mcpAPI.callTool(`${serverName}__${selected}`, parsed);
      setResult({ ok: true, data: data.result });
    } catch (err) {
      setResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "none", border: "none", cursor: "pointer",
          fontSize: 12, fontWeight: 600, color: C.su, padding: 0,
        }}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        Tool Tester
      </button>

      {open && (
        <div style={{
          marginTop: 10, padding: 14, background: C.bg, borderRadius: 10,
          border: `1px solid ${C.bord}`, display: "flex", flexDirection: "column", gap: 10,
        }}>
          {tools.length === 0 ? (
            <div style={{ fontSize: 12, color: C.mu }}>No tools cached. Run "Test connection" first.</div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.su }}>Tool</span>
                <select
                  value={selected}
                  onChange={e => setSelected(e.target.value)}
                  style={{ padding: "6px 10px", border: `1px solid ${C.bord}`, borderRadius: 8, fontSize: 13, color: C.tx }}
                >
                  {tools.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
                {selectedTool?.description && (
                  <div style={{ fontSize: 11, color: C.mu }}>{selectedTool.description}</div>
                )}
              </div>

              {/* Schema hint table */}
              {Object.keys(schemaProps).length > 0 && (
                <div style={{ border: `1px solid ${C.bord}`, borderRadius: 8, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: "var(--lyra-color-surface-2)" }}>
                        <th style={{ padding: "5px 8px", textAlign: "left", color: C.su, fontWeight: 600, borderBottom: `1px solid ${C.bord}` }}>Field</th>
                        <th style={{ padding: "5px 8px", textAlign: "left", color: C.su, fontWeight: 600, borderBottom: `1px solid ${C.bord}` }}>Type</th>
                        <th style={{ padding: "5px 8px", textAlign: "left", color: C.su, fontWeight: 600, borderBottom: `1px solid ${C.bord}` }}>Default</th>
                        <th style={{ padding: "5px 8px", textAlign: "left", color: C.su, fontWeight: 600, borderBottom: `1px solid ${C.bord}` }}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(schemaProps).map(([key, schema], i) => (
                        <tr key={key} style={{ background: i % 2 === 0 ? "var(--lyra-color-surface)" : "var(--lyra-color-surface-2)" }}>
                          <td style={{ padding: "5px 8px", borderBottom: `1px solid ${C.bord}` }}>
                            <code style={{ fontFamily: "monospace", color: C.tx }}>{key}</code>
                            {required.has(key) && <span style={{ color: C.red, marginLeft: 2 }}>*</span>}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: `1px solid ${C.bord}`, color: "var(--lyra-color-primary)" }}>
                            <code style={{ fontFamily: "monospace" }}>{schema.type || schema.enum ? (schema.enum ? "enum" : schema.type) : "any"}</code>
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: `1px solid ${C.bord}`, color: C.mu }}>
                            {schema.default !== undefined
                              ? <code style={{ fontFamily: "monospace" }}>{JSON.stringify(schema.default)}</code>
                              : <span style={{ color: "var(--app-border-strong)" }}>—</span>
                            }
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: `1px solid ${C.bord}`, color: C.mu }}>
                            {schema.description || (schema.enum ? schema.enum.join(" | ") : "")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.su }}>Args (JSON)</span>
                <textarea
                  value={args}
                  onChange={e => setArgs(e.target.value)}
                  rows={4}
                  style={{
                    padding: "7px 10px", border: `1px solid ${C.bord}`, borderRadius: 8,
                    fontSize: 12, fontFamily: "monospace", color: C.tx, resize: "vertical",
                  }}
                />
              </div>

              <Btn label="Run tool" icon={Play} loading={loading} onClick={handleCall} small />

              {result && (
                <div style={{
                  padding: 10, borderRadius: 8, fontSize: 12, fontFamily: "monospace",
                  background: result.ok ? "var(--lyra-color-success-bg)" : "var(--lyra-color-danger-bg)",
                  border: `1px solid ${result.ok ? "var(--app-success-border)" : "var(--app-danger-border)"}`,
                  color: result.ok ? "var(--lyra-color-success)" : C.red,
                  whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 200, overflowY: "auto",
                }}>
                  {result.ok ? JSON.stringify(result.data, null, 2) : result.error}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Server Card ───────────────────────────────────────────────────────────────
function ServerCard({ server, onDeleted, onUpdated, isMobile = false }) {
  const [testing, setTesting] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const TransIcon = TRANSPORTS[server.transport]?.icon || Server;

  const handleTest = async () => {
    setTesting(true);
    try {
      const data = await mcpAPI.testServer(server._id);
      toast.success(`Connected — ${data.toolCount} tool${data.toolCount !== 1 ? "s" : ""} found`);
      onUpdated({ ...server, tools: data.tools, lastTestedAt: new Date().toISOString(), lastError: null });
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      toast.error(`Connection failed: ${msg}`);
      onUpdated({ ...server, lastError: msg, lastTestedAt: new Date().toISOString() });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove "${server.displayName}"?`)) return;
    try {
      await mcpAPI.deleteServer(server._id);
      toast.success("Server removed");
      onDeleted(server._id);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  };
  const handleOAuth = async () => {
    setAuthorizing(true);
    try { const { authorizeUrl } = await mcpAPI.startOAuth(server._id); window.location.assign(authorizeUrl); }
    catch (err) { toast.error(err.response?.data?.error || err.message); setAuthorizing(false); }
  };

  const connected = !!server.lastTestedAt && !server.lastError;
  const toolCount = server.tools?.length || 0;

  return (
    <div style={{
      background: C.card, border: `1.5px solid ${connected ? C.blueBord : C.bord}`,
      borderRadius: 14, padding: isMobile ? "12px" : "18px 20px",
      boxShadow: connected ? "0 0 0 3px var(--app-primary-soft)" : "0 1px 4px rgba(0,0,0,0.05)",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 0 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: connected ? C.blueBg : "var(--app-surface-3)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <TransIcon size={18} color={connected ? C.blue : C.su} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.tx }}>{server.displayName}</span>
              <Badge label={TRANSPORTS[server.transport]?.label || server.transport} bg="var(--app-surface-3)" color={C.su} />
              {connected
                ? <Badge label="CONNECTED" bg={C.greenBg} color={C.green} />
                : server.lastTestedAt
                  ? <Badge label="ERROR" bg={C.redBg} color={C.red} />
                  : <Badge label="NOT TESTED" bg="var(--app-surface-3)" color={C.mu} />
              }
            </div>
            <div style={{ fontSize: 12, color: C.mu, marginTop: 2 }}>
              {server.transport === "stdio"
                ? <code style={{ fontFamily: "monospace", fontSize: 11 }}>{server.command} {(server.args || []).join(" ")}</code>
                : <code style={{ fontFamily: "monospace", fontSize: 11 }}>{server.url}</code>
              }
            </div>
            {server.description && (
              <div style={{ fontSize: 12, color: C.su, marginTop: 2 }}>{server.description}</div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, flexShrink: 0, width: isMobile ? "100%" : "auto", justifyContent: isMobile ? "flex-end" : "flex-start" }}>
          {(server.transport === 'http' || server.transport === 'sse') && <Btn icon={Key} label={authorizing ? "Connecting…" : "Connect with OAuth"} loading={authorizing} onClick={handleOAuth} variant="ghost" small={isMobile} />}
          <Btn
            icon={RefreshCw} label={testing ? "Testing…" : "Test connection"}
            loading={testing} onClick={handleTest} variant="ghost" small={isMobile}
          />
          <Btn icon={Trash2} label={isMobile ? "Remove" : ""} onClick={handleDelete} variant="danger" small={isMobile} />
        </div>
      </div>

      {/* Tool chips */}
      {toolCount > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {server.tools.slice(0, 8).map(t => (
            <span key={t.name} style={{
              fontSize: 11, padding: "2px 8px", borderRadius: 6,
              background: "var(--app-primary-soft)", color: "var(--app-primary-deep)", border: "1px solid var(--app-primary-soft-2)",
              fontFamily: "monospace",
            }}>{t.name}</span>
          ))}
          {toolCount > 8 && (
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: C.bg, color: C.mu }}>
              +{toolCount - 8} more
            </span>
          )}
        </div>
      )}

      {/* Error */}
      {server.lastError && (
        <div style={{
          marginTop: 10, padding: "8px 10px", background: C.redBg, borderRadius: 8,
          display: "flex", gap: 6, alignItems: "flex-start",
        }}>
          <AlertCircle size={13} color={C.red} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: C.red }}>{server.lastError}</span>
        </div>
      )}

      {/* Tool tester — shows by slug name so POST /api/mcp/call resolves it */}
      {connected && toolCount > 0 && <ToolTester serverId={server._id} serverName={server.name} isMobile={isMobile} />}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MCPServersPage() {
  const [servers, setServers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 768 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mcpAPI.listServers();
      setServers(data.servers || []);
    } catch (err) {
      toast.error("Failed to load MCP servers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdded   = (s) => setServers(prev => [s, ...prev]);
  const handleDeleted = (id) => setServers(prev => prev.filter(s => s._id !== id));
  const handleUpdated = (updated) => setServers(prev => prev.map(s => s._id === updated._id ? updated : s));

  return (
    <div style={{ minHeight: "100vh", background: "var(--lyra-color-surface)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: isMobile ? "14px 10px 40px" : "28px 32px 60px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: isMobile ? 16 : 28, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 0 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{ width: 36, height: 36, background: "var(--app-primary-soft)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Cpu size={18} color="var(--lyra-color-primary)" />
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", margin: 0 }}>MCP Servers</h1>
          </div>
          <p style={{ fontSize: 13, color: "var(--lyra-color-text-muted)", margin: 0 }}>
            Connect Lyra to external MCP servers. Their tools become available to all your agents.
          </p>
        </div>
        <div style={{ width: isMobile ? "100%" : "auto" }}>
          <Btn icon={Plus} label="Add server" onClick={() => setShowAdd(true)} />
        </div>
      </div>

      {/* Personal MCP Server */}
      <PersonalMcpPanel isMobile={isMobile} />

      {/* Featured integrations */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.mu, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>
          Featured integrations
        </div>
        <FigmaPreset
          servers={servers}
          isMobile={isMobile}
          onAdded={(s) => setServers(prev => {
            const exists = prev.some(x => x._id === s._id);
            if (exists) return prev.map(x => x._id === s._id ? s : x);
            return [s, ...prev];
          })}
          onRemoved={() => setServers(prev => prev.filter(x => x.name !== "figma"))}
        />
      </div>

      {/* Quick-start guide */}
      <div style={{
        background: "var(--app-primary-soft)", border: "1px solid var(--app-primary-soft-2)", borderRadius: 12,
        padding: "14px 16px", marginBottom: 24,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--app-primary-deep)", marginBottom: 8 }}>Quick start</div>
        <div style={{ fontSize: 12, color: "var(--app-text-2)", lineHeight: 1.8, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ overflowX: "auto" }}>
            <strong>Local test server (SSE):</strong>&nbsp;
            <code style={{ background: "var(--lyra-color-surface-2)", padding: "1px 6px", borderRadius: 4, fontFamily: "monospace" }}>
              npx -y @modelcontextprotocol/server-everything --transport sse --port 3010
            </code>
            &nbsp;→ register as SSE → <code style={{ fontFamily: "monospace" }}>http://localhost:3010/sse</code>
          </div>
          <div style={{ overflowX: "auto" }}>
            <strong>GitHub Copilot MCP (HTTP):</strong>&nbsp;
            URL: <code style={{ fontFamily: "monospace", background: "var(--lyra-color-surface-2)", padding: "1px 6px", borderRadius: 4 }}>https://api.githubcopilot.com/mcp/</code>
            &nbsp; Headers: <code style={{ fontFamily: "monospace", background: "var(--lyra-color-surface-2)", padding: "1px 6px", borderRadius: 4 }}>{`{"Authorization":"Bearer YOUR_GITHUB_TOKEN","Copilot-Integration-Id":"vscode-chat"}`}</code>
          </div>
        </div>
      </div>

      {/* Server list */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Loader2 size={24} color={C.mu} style={{ animation: "spin 1s linear infinite" }} />
        </div>
      ) : servers.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: "var(--lyra-color-surface)", borderRadius: 16, border: `1.5px dashed ${C.bord}`,
        }}>
          <Server size={36} color={C.mu} style={{ margin: "0 auto 12px" }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: C.tx }}>No MCP servers yet</div>
          <div style={{ fontSize: 13, color: C.mu, marginTop: 4, marginBottom: 16 }}>
            Register your first server to give Lyra's agents access to external tools
          </div>
          <Btn icon={Plus} label="Add your first server" onClick={() => setShowAdd(true)} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {servers.map(s => (
            <ServerCard
              key={s._id}
              server={s}
              isMobile={isMobile}
              onDeleted={handleDeleted}
              onUpdated={handleUpdated}
            />
          ))}
        </div>
      )}

      {showAdd && <AddServerModal onClose={() => setShowAdd(false)} onAdded={handleAdded} isMobile={isMobile} />}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
